'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { DollarSign, Loader2 } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { useAuth } from '@/lib/AuthContext';
import { getTransactions } from '@/lib/transactions';
import type { Transaction } from '@/lib/supabase';

export default function RecentActivityFeed() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const data = await getTransactions(user.id);
      setTransactions(data.slice(0, 8));
    } catch {
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-base font-600 text-foreground">Recent Activity</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Latest transactions</p>
        </div>
      </div>

      <div className="divide-y divide-border/40">
        {loading ? (
          <div className="px-5 py-8 flex items-center justify-center">
            <Loader2 size={20} className="text-accent animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <DollarSign size={24} className="text-muted-foreground mx-auto mb-2 opacity-40" />
            <p className="text-xs text-muted-foreground">No transactions yet — upload a document</p>
          </div>
        ) : (
          transactions.map((tx) => (
            <div key={tx.id} className="flex items-center gap-3 px-5 py-3 hover:bg-muted/20 transition-colors">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                tx.transaction_type === 'Income' ? 'bg-success/15' : 'bg-muted'
              }`}>
                <DollarSign size={14} className={tx.transaction_type === 'Income' ? 'text-success' : 'text-muted-foreground'} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-500 text-foreground truncate">{tx.merchant}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] text-muted-foreground">{tx.date}</span>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <StatusBadge variant={tx.source === 'Personal' ? 'personal' : 'business'} size="sm">
                    {tx.source}
                  </StatusBadge>
                </div>
              </div>
              <p className={`text-sm font-700 font-mono-nums flex-shrink-0 ${Number(tx.amount) > 0 ? 'text-success' : 'text-foreground'}`}>
                {Number(tx.amount) > 0 ? '+' : ''}{Number(tx.amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}